// Typed fetch wrapper for VendorPayDio API
// Tokens stored in module scope (not localStorage) due to iframe constraint

// --- Types ---

interface ApiErrorBody {
  code: string;
  message: string;
}

interface ApiResponse<T> {
  data: T | null;
  error: ApiErrorBody | null;
  meta: Record<string, unknown>;
}

interface Address {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface Vendor {
  id: string;
  email: string;
  companyName: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone: string;
  billingAddress: Address;
  mailingAddress?: Address;
  createdAt: string;
  updatedAt: string;
}

export type OrderType =
  | "bcb"
  | "badge_attested"
  | "mixed"
  | "badge_replacement"
  | "completion";

/** Mirror of the server's ORDER_TYPE_LABELS (src/api/src/lib/order-products.ts).
 *  Keep the strings verbatim — receipts and emails use the same wording. */
export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  bcb: "Background Check + Badge",
  badge_attested: "Badge — attested as already cleared",
  mixed: "Background checks + badges (mixed)",
  badge_replacement: "Replacement badge",
  completion: "Background check completion",
};

export type EmployeeStatus =
  | "attested_pending_verification"
  | "attestation_not_found"
  | "paid"
  | "directed_to_bim"
  | "fingerprint_appointment"
  | "background_cleared"
  | "entered_in_systems"
  | "badge_issued"
  | "badge_delivered";

export interface Employee {
  id: string;
  vendorId: string;
  firstName: string;
  lastName: string;
  track?: "bcb" | "attested";
  status: EmployeeStatus;
  inactive?: boolean;
  clearanceDate?: string;
  photoOnFile?: boolean;
  /** Dollars still owed to complete a not-found attestation. */
  completionPriceOwed?: number;
  createdByOrderId?: string;
  createdAt: string;
  updatedAt: string;
  /** Roster responses only: a replacement badge is already on order for them
   *  (the server refuses a second until it is made). Absent on the employees
   *  an order response carries. */
  replacementPending?: boolean;
}

export interface OrderLine {
  product: "bcb" | "badge_attested";
  quantity: number;
  unitPrice: number;
}

export interface Order {
  id: string;
  vendorId: string;
  /** Absent on orders stored before v2: those are bcb orders. */
  type?: OrderType;
  quantity: number;
  /** 0 on a mixed order: `lines` is authoritative there. */
  unitPrice: number;
  totalAmount: number;
  /** Creation orders: the per-product itemization. Absent on legacy and
   *  referencing (replacement/completion) orders. */
  lines?: OrderLine[];
  /** Referencing orders: the employees the order is about. */
  employeeIds?: string[];
  fulfilledEmployeeIds?: string[];
  attestation?: { acceptedAt: string; copyVersion: string };
  paymentMethod: "credit_card" | "ach";
  vancoTransactionId: string;
  paymentMethodLast4: string;
  status: "paid" | "processing" | "completed";
  createdAt: string;
  updatedAt: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  companyName: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone: string;
  billingAddress: Address;
  mailingAddress?: Address;
}

export interface LoginInput {
  email: string;
  password: string;
}

/** The payment fields every order type carries. */
export interface OrderPaymentFields {
  /** Card-only while ACH is deferred (2026-07-22) — see
   *  decisions/2026-07-22-defer-ach-card-only-rebuild.md. */
  paymentMethod: "credit_card";
  /**
   * UUID identifying this checkout submission, persisted in sessionStorage so it
   * survives a reload -- see lib/idempotency-storage.ts. Sent to Vanco as both the
   * Idempotency-Key and the invoiceReference, and used as the id of the server's
   * durable outcome record, so one value ties the browser, our database and
   * Vanco's History together.
   *
   * NEVER rotated on an ambiguous outcome. It rotates only on CONFIRMED outcomes
   * (PAYMENT_FAILED / RETRY_NOW / STALE_KEY) and is cleared once an order is
   * confirmed.
   */
  idempotencyKey: string;
  /** The card's billing address (spec 2026-07-30). Optional only for the
   *  server's stale-client bridge; this client always sends it. */
  billingAddress?: { street: string; city: string; state: string; zip: string };
  card?: {
    cardNumber: string;
    expireMonth: string;
    expireYear: string;
    cvv?: string;
    nameOnCard?: string;
  };
}

export interface EmployeeEntry {
  firstName: string;
  lastName: string;
  /** Per-row product choice on a creation order; the server defaults to bcb. */
  product?: "bcb" | "badge_attested";
}

/** Mirrors the server's discriminated createOrderSchema. */
export type CreateOrderInput =
  | (OrderPaymentFields & {
      type: "employee_addition";
      employees: EmployeeEntry[];
      attestationAccepted?: true;
    })
  | (OrderPaymentFields & { type: "badge_replacement"; employeeIds: string[] })
  | (OrderPaymentFields & { type: "completion"; employeeIds: string[] });

// --- Token management ---

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _onAuthFailure: (() => void) | null = null;

export function setTokens(access: string, refresh: string): void {
  _accessToken = access;
  _refreshToken = refresh;
}

export function clearTokens(): void {
  _accessToken = null;
  _refreshToken = null;
}

export function onAuthFailure(callback: () => void): void {
  _onAuthFailure = callback;
}

// --- Error classes ---

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

export class AuthError extends ApiError {
  constructor(message: string, code: string = "AUTH_ERROR", status: number = 401) {
    super(message, code, status);
    this.name = "AuthError";
  }
}

// --- Core fetch ---

let _refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  if (!_refreshToken) {
    throw new AuthError("No refresh token available");
  }

  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: _refreshToken }),
  });

  let body: ApiResponse<{ accessToken: string; refreshToken: string }>;
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // A gateway error page, not an answer about the token. Keep the tokens.
    throw new ApiError(`Request failed (${response.status})`, "NON_JSON_RESPONSE", response.status);
  }

  if (!response.ok || body.error) {
    const message = body.error?.message ?? "Token refresh failed";
    const code = body.error?.code ?? "REFRESH_FAILED";
    // Only a 401 says the refresh token itself was rejected. Anything else
    // (503 AUTH_UNAVAILABLE, 429, a 5xx) is the service failing, and must not
    // log a vendor out — possibly in the middle of a checkout.
    if (response.status === 401) {
      clearTokens();
      throw new AuthError(message, code, 401);
    }
    throw new ApiError(message, code, response.status);
  }

  // body.data is non-null here: the guard above throws on !response.ok || body.error
  _accessToken = body.data!.accessToken;
  _refreshToken = body.data!.refreshToken;
}

export async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
): Promise<{ data: T; meta: Record<string, unknown> }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };

  // A 401 can only end a session that exists. A failed login is a 401 too, and
  // it must not run the session-end handler.
  const hadSession = _accessToken !== null || _refreshToken !== null;

  if (_accessToken) {
    // Use X-VPD-Auth because Azure SWA overwrites the standard Authorization header
    headers["X-VPD-Auth"] = `Bearer ${_accessToken}`;
  }

  let response = await fetch(url, { ...options, headers });

  // Handle 401 with refresh token retry
  if (response.status === 401 && _refreshToken) {
    try {
      // Deduplicate concurrent refresh attempts
      if (!_refreshPromise) {
        _refreshPromise = refreshAccessToken().finally(() => {
          _refreshPromise = null;
        });
      }
      await _refreshPromise;

      // Retry original request with new token
      headers["X-VPD-Auth"] = `Bearer ${_accessToken}`;
      response = await fetch(url, { ...options, headers });
    } catch (refreshErr) {
      // End the session only when the refresh token was actually rejected.
      // An outage or a network error leaves the session alone and surfaces as
      // an ordinary request failure.
      if (refreshErr instanceof AuthError) {
        _onAuthFailure?.();
        throw new AuthError("Session expired. Please log in again.");
      }
      throw refreshErr;
    }
  }

  let body: ApiResponse<T>;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    // Server returned non-JSON (e.g., 502 from Azure SWA edge)
    throw new ApiError(
      `Request failed (${response.status})`,
      "NON_JSON_RESPONSE",
      response.status,
    );
  }

  if (!response.ok || body.error) {
    const errorMessage = body.error?.message ?? "An unexpected error occurred";
    const errorCode = body.error?.code ?? "API_ERROR";

    if (response.status === 401) {
      if (hadSession) {
        _onAuthFailure?.();
      }
      throw new AuthError(errorMessage, errorCode, 401);
    }
    throw new ApiError(errorMessage, errorCode, response.status);
  }

  // body.data is non-null here: the guard above throws on !response.ok || body.error
  return { data: body.data as T, meta: body.meta };
}

// --- Typed endpoint functions ---

interface AuthResponse {
  vendor: Vendor;
  accessToken: string;
  refreshToken: string;
}

export async function apiRegister(input: RegisterInput): Promise<AuthResponse> {
  const { data } = await apiFetch<AuthResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function apiLogin(input: LoginInput): Promise<AuthResponse> {
  const { data } = await apiFetch<AuthResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function apiForgotPassword(email: string): Promise<{ message: string }> {
  const { data } = await apiFetch<{ message: string }>("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  return data;
}

export async function apiResetPassword(
  token: string,
  password: string,
): Promise<{ message: string }> {
  const { data } = await apiFetch<{ message: string }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
  return data;
}

export interface CreateOrderResponse {
  order: Order;
  employees: Employee[];
  vancoTransactionId: string;
}

export async function apiCreateOrder(input: CreateOrderInput): Promise<CreateOrderResponse> {
  const { data } = await apiFetch<CreateOrderResponse>("/api/orders", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

interface GetOrdersResponse {
  orders: Order[];
}

export async function apiGetOrders(): Promise<{
  orders: Order[];
  meta: Record<string, unknown>;
}> {
  const { data, meta } = await apiFetch<GetOrdersResponse>("/api/orders");
  return { orders: data.orders, meta };
}

interface GetOrderDetailResponse {
  order: Order;
  employees: Employee[];
}

export async function apiGetOrder(id: string): Promise<GetOrderDetailResponse> {
  const { data } = await apiFetch<GetOrderDetailResponse>(`/api/orders/${id}`);
  return data;
}

export async function apiGetPrice(): Promise<{ unitPrice: number }> {
  const { data } = await apiFetch<{ unitPrice: number }>("/api/config/price");
  return data;
}

/** Public: both product prices. Needs no session. */
export async function apiGetPrices(): Promise<{ bcbPrice: number; badgePrice: number }> {
  const { data } = await apiFetch<{ bcbPrice: number; badgePrice: number }>("/api/config/prices");
  return data;
}

export async function apiGetRoster(): Promise<{ employees: Employee[] }> {
  const { data } = await apiFetch<{ employees: Employee[] }>("/api/employees");
  return data;
}

export async function apiUpdateEmployee(
  id: string,
  inactive: boolean,
): Promise<{ employee: Employee }> {
  const { data } = await apiFetch<{ employee: Employee }>(
    `/api/employees/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ inactive }) },
  );
  return data;
}
